import axios from 'axios'
import { call, select, put, takeEvery, takeLatest, type SagaGenerator } from 'typed-redux-saga'
import { api } from 'lib/api'
import { errorWrap } from 'lib/saga-helpers'
import type { PayloadAction, RootState } from 'store/types'
import { constants } from './actions'
import type { FetchNotificationItemsPayload, MarkCollectionReadPayload, SetNotificationPayload } from './actions'
import type { CollectionNewCount, FeedResponse, Notification, NotificationItemStatus } from './types'
import { errorToast, successToast, warnToast } from '../../lib/toast'

// notifications is a static slice, so it is always present on RootState.
const selectors = {
  pageNumber: ({ notifications }: RootState) => notifications.notificationItemsPage,
  filters: ({ notifications }: RootState) => notifications.notificationItemsFilters
}

function* fetchNotifications(): SagaGenerator<void> {
  const { data } = yield* call(api.get<Notification[]>, '/notifications/notifications')
  yield* put({
    type: constants.FETCH_NOTIFICATIONS_SUCCESS,
    payload: data
  })
}

function* fetchNotificationItems({ payload }: PayloadAction<FetchNotificationItemsPayload>): SagaGenerator<void> {
  const pageNumber = yield* select(selectors.pageNumber)
  const filters = yield* select(selectors.filters)
  const params = {
    offset: (pageNumber - 1 || 0) * CONFIG.notificationItemsPaginationLimit,
    limit: CONFIG.notificationItemsPaginationLimit,
    filters: JSON.stringify(filters || {}),
    order_by_oldest: payload?.sortByOldest || false
  }
  const { data } = yield* call(api.get<FeedResponse>, '/notifications/feed', { params })
  yield* put({
    type: constants.FETCH_NOTIFICATION_ITEMS_SUCCESS,
    payload: data
  })
}

// The reducer has already incremented notificationItemsPage, so the offset comes from the
// store. That math is only exact because /notifications/feed prepends scite-wide notices
// (shrinking the limit) when filters.notificationIDs is empty — collection feeds always set it.
export function* fetchMoreNotificationItems(): SagaGenerator<void> {
  const pageNumber = yield* select(selectors.pageNumber)
  const filters = yield* select(selectors.filters)
  const params = {
    offset: Math.max(0, pageNumber - 1) * CONFIG.notificationItemsPaginationLimit,
    limit: CONFIG.notificationItemsPaginationLimit,
    filters: JSON.stringify(filters || {}),
    order_by_oldest: false
  }
  const { data } = yield* call(api.get<FeedResponse>, '/notifications/feed', { params })
  yield* put({
    type: constants.FETCH_MORE_NOTIFICATION_ITEMS_SUCCESS,
    payload: data
  })
}

function* fetchDropDownNotificationItems(): SagaGenerator<void> {
  const params = {
    offset: 0,
    limit: CONFIG.notificationItemsDropDownPaginationLimit,
    new_only: true,
    metadata_only: true
  }
  const { data } = yield* call(api.get<FeedResponse>, '/notifications/feed', { params })
  yield* put({
    type: constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS_SUCCESS,
    payload: data
  })
}

function* fetchCollectionNewCounts(): SagaGenerator<void> {
  const { data } = yield* call(api.get<CollectionNewCount[]>, '/notifications/feed/collection-counts')
  yield* put({
    type: constants.FETCH_COLLECTION_NEW_COUNTS_SUCCESS,
    payload: data
  })
}

function* setNotification({ payload }: PayloadAction<SetNotificationPayload>): SagaGenerator<void> {
  const { data } = yield* call(api.post<Notification>, '/notifications/notifications', payload)
  yield* put({
    type: constants.SET_NOTIFICATION_SUCCESS,
    payload: { notification: data }
  })

  if (payload.warningMessage) {
    warnToast({ message: payload.warningMessage, durationMs: 5000, showIcon: true })
  } else {
    successToast({ message: 'Successfully set notification.', durationMs: 3000 })
  }
}

function* updateNotification({ payload }: PayloadAction<Notification>): SagaGenerator<void> {
  yield* call(api.put, '/notifications/notifications', payload)
  yield* put({ type: constants.UPDATE_NOTIFICATION_SUCCESS })
}

function* setNotificationItemsStatus({ payload }: PayloadAction<NotificationItemStatus[]>): SagaGenerator<void> {
  yield* call(api.post, '/notifications/feed', payload)
  yield* put({
    type: constants.SET_NOTIFICATION_ITEMS_STATUS_SUCCESS,
    payload: { notificationItemStatuses: payload }
  })
}

function* markAllNotificationItemsRead(): SagaGenerator<void> {
  yield* call(api.post, '/notifications/feed/mark-all-read')
  yield* put({ type: constants.MARK_ALL_NOTIFICATION_ITEMS_READ_SUCCESS })
}

export function* markCollectionNotificationsRead({
  payload
}: PayloadAction<MarkCollectionReadPayload>): SagaGenerator<void> {
  yield* call(api.post, '/notifications/feed/mark-read', { notificationIds: payload.notificationIDs })
  yield* put({ type: constants.MARK_COLLECTION_NOTIFICATIONS_READ_SUCCESS })
  // The bell count is global, so re-sync rather than guess this collection's share.
  yield* put({ type: constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS })
}

function* deleteNotifications({ payload: ids }: PayloadAction<number[]>): SagaGenerator<void> {
  for (const id of ids) {
    yield* call(api.delete, `/notifications/notifications/${id}`)
  }
  yield* put({ type: constants.DELETE_NOTIFICATIONS_SUCCESS, payload: ids })

  const msg = ids.length > 1 ? 'Successfully removed notifications.' : 'Successfully removed notification.'
  successToast({
    message: msg,
    durationMs: 3000
  })
}

export default function* rootSaga(): SagaGenerator<void> {
  yield* takeLatest(constants.FETCH_NOTIFICATIONS, errorWrap(fetchNotifications, constants.FETCH_NOTIFICATIONS_FAILURE))
  yield* takeLatest(
    constants.SET_NOTIFICATION,
    errorWrap(setNotification, null, function* (e: unknown, { payload }: PayloadAction<SetNotificationPayload>) {
      errorToast({
        message: 'Unexpected error setting notification.',
        durationMs: 3000
      })
      yield* put({
        type: constants.SET_NOTIFICATION_FAILURE,
        error: e,
        payload: {
          error: axios.isAxiosError(e) ? e.response?.data?.detail || '' : '',
          source: payload.source,
          category: payload.category
        }
      })
    })
  )
  yield* takeLatest(
    constants.DELETE_NOTIFICATIONS,
    errorWrap(deleteNotifications, constants.DELETE_NOTIFICATIONS_FAILURE, function* () {
      errorToast({
        message: 'Unexpected error removing notification.',
        durationMs: 3000
      })
      yield* put({
        type: constants.DELETE_NOTIFICATIONS_FAILURE
      })
    })
  )
  yield* takeLatest(
    constants.FETCH_NOTIFICATION_ITEMS,
    errorWrap(fetchNotificationItems, constants.FETCH_NOTIFICATION_ITEMS_FAILURE)
  )
  yield* takeLatest(
    constants.FETCH_MORE_NOTIFICATION_ITEMS,
    errorWrap(fetchMoreNotificationItems, constants.FETCH_MORE_NOTIFICATION_ITEMS_FAILURE)
  )
  yield* takeLatest(
    constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS,
    errorWrap(fetchDropDownNotificationItems, constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS_FAILURE)
  )
  yield* takeLatest(
    constants.SET_NOTIFICATION_ITEMS_STATUS,
    errorWrap(setNotificationItemsStatus, constants.SET_NOTIFICATION_ITEMS_STATUS_FAILURE)
  )
  yield* takeLatest(
    constants.MARK_ALL_NOTIFICATION_ITEMS_READ,
    errorWrap(markAllNotificationItemsRead, null, function* () {
      errorToast({
        message: 'Unexpected error marking all as read.',
        durationMs: 3000
      })
      // The reducer cleared the badge optimistically; re-sync with the server
      // so a failed request doesn't leave a stale zeroed count.
      yield* put({ type: constants.MARK_ALL_NOTIFICATION_ITEMS_READ_FAILURE })
      yield* put({ type: constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS })
      yield* put({ type: constants.FETCH_NOTIFICATION_ITEMS })
    })
  )
  // takeEvery, not takeLatest: cancelling this write would clear the badge locally while the
  // server still has the items NEW.
  yield* takeEvery(
    constants.MARK_COLLECTION_NOTIFICATIONS_READ,
    errorWrap(markCollectionNotificationsRead, null, function* () {
      yield* put({ type: constants.MARK_COLLECTION_NOTIFICATIONS_READ_FAILURE })
      yield* put({ type: constants.FETCH_COLLECTION_NEW_COUNTS })
    })
  )
  yield* takeLatest(constants.UPDATE_NOTIFICATION, errorWrap(updateNotification, constants.UPDATE_NOTIFICATION_FAILURE))
  yield* takeLatest(
    constants.FETCH_COLLECTION_NEW_COUNTS,
    errorWrap(fetchCollectionNewCounts, constants.FETCH_COLLECTION_NEW_COUNTS_FAILURE)
  )
}
